import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Users, Warning, CurrencyEur } from "@phosphor-icons/react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";
import SeasonSelector from "@/components/SeasonSelector";
import { logger } from "@/lib/logger";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtNum = (n) => (n || 0).toLocaleString("es-ES");

const TOOLTIP = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [filterTemporada, setFilterTemporada] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const params = {};
        if (filterTemporada) params.temporada = filterTemporada;
        const [ig, mem] = await Promise.all([
          api.get("/info-general", { params }),
          api.get("/members", { params: { estado: "pendiente" } }),
        ]);
        setData(ig.data);
        setPendientes(mem.data);
      } catch (e) { logger.warn("Dashboard load failed:", e?.message); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [filterTemporada]);

  const byMatch = data?.by_match || [];
  const top = data?.top_match;
  const tot = data?.totales || {};
  const socios = data?.socios || {};
  const inc = data?.incidencias || {};

  // Datos para gráficos
  const chartAsistencia = byMatch.map(m => ({ name: `vs ${m.rival.slice(0, 10)}`, asistencia: m.asistencia }));
  const chartEconomia = byMatch.map(m => ({ name: `vs ${m.rival.slice(0, 10)}`, ingresos: m.ingresos, gastos: m.gastos, beneficio: m.beneficio }));

  // Tipos de entradas por partido (stacked)
  const tiposKeys = ["socio", "general", "infantil", "invitacion", "prensa", "directiva", "patrocinador", "jugadores_cantera"];
  const tipoColors = { socio: "#22C55E", general: "#2563EB", infantil: "#F97316", invitacion: "#A855F7", prensa: "#06B6D4", directiva: "#EAB308", patrocinador: "#EC4899", jugadores_cantera: "#14B8A6" };
  const chartTipos = byMatch.map(m => ({ name: `vs ${m.rival.slice(0, 10)}`, ...tiposKeys.reduce((a, k) => ({ ...a, [k]: m.tipos?.[k] || 0 }), {}) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">Panel General</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Información General</h1>
          <p className="text-sm text-slate-500 mt-1">Estadísticas agregadas por temporada.</p>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-1">Temporada</div>
          <SeasonSelector value={filterTemporada} onChange={setFilterTemporada} includeAll size="md" testid="dashboard-season-filter" />
        </div>
      </div>

      {/* Totales rápidos */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Partidos" value={fmtNum(tot.partidos)} icon={Trophy} color="#2563EB" />
        <Kpi label="Socios activos" value={fmtNum(socios.activos)} icon={Users} color="#22C55E" />
        <Kpi label="Beneficio total" value={fmtEUR(tot.beneficio)} icon={CurrencyEur} color={tot.beneficio >= 0 ? "#16a34a" : "#dc2626"} />
        <Kpi label="Incidencias abiertas" value={fmtNum(inc.abiertas)} icon={Warning} color="#ea580c" />
      </div>

      {/* Fila 1: Resumen aforos + Ec. ingresos/gastos */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Resumen de aforos por partido">
          {byMatch.length === 0 ? <Empty /> : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={chartAsistencia}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} />
                  <Bar dataKey="asistencia" fill="#2563EB" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Ingresos y gastos por partido">
          {byMatch.length === 0 ? <Empty /> : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={chartEconomia}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} formatter={(v) => fmtEUR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="ingresos" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-slate-600">Total ingresos: <strong className="text-[#16a34a]">{fmtEUR(tot.ingresos)}</strong></span>
            <span className="text-slate-600">Total gastos: <strong className="text-[#dc2626]">{fmtEUR(tot.gastos)}</strong></span>
          </div>
        </Card>
      </div>

      {/* Fila 2: Afluencia line + Top partido + Socios pendientes */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Afluencia por partidos">
          {byMatch.length === 0 ? <Empty /> : (
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={chartAsistencia}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} />
                  <Line type="monotone" dataKey="asistencia" stroke="#2563EB" strokeWidth={2} dot={{ fill: "#2563EB" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Partido con más afluencia">
          {!top ? <Empty /> : (
            <div className="text-center py-4">
              <Trophy size={48} className="mx-auto text-[#EAB308]" weight="fill" />
              <div className="font-display font-bold text-3xl uppercase mt-3 leading-tight">vs {top.rival}</div>
              <div className="text-slate-500 text-sm mt-1">{top.fecha}</div>
              <div className="font-display font-black text-6xl tracking-tighter mt-4 text-[#2563EB]">{fmtNum(top.asistencia)}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">asistentes ({top.ocupacion}% aforo)</div>
            </div>
          )}
        </Card>

        <Card title="Socios pendientes de pago" subtitle={`${pendientes.length} socios en estado pendiente`}>
          {pendientes.length === 0 ? <Empty text="Todos al día ✓" /> : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {pendientes.slice(0, 20).map(s => (
                <Link key={s.id} to={`/socios/${s.id}`}
                  className="flex justify-between items-center px-3 py-2 hover:bg-slate-50 border border-slate-100"
                  style={{borderRadius:8}}>
                  <span className="text-sm">#{s.numero_socio} {s.nombre} {s.apellidos}</span>
                  <span className="text-[10px] text-[#ea580c] font-bold uppercase tracking-widest">Pendiente</span>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Fila 3: Tipos de entrada + Incidencias */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Resumen de tipos de entrada por partido" subtitle="Socios vs generales vs otros">
          {chartTipos.length === 0 ? <Empty /> : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={chartTipos}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {tiposKeys.map(k => <Bar key={k} dataKey={k} stackId="a" fill={tipoColors[k]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Resumen de incidencias" subtitle={`${inc.abiertas || 0} abiertas · ${inc.resueltas || 0} resueltas`}>
          <div className="flex gap-2 mb-3">
            <button onClick={() => nav("/incidencias")} className="text-xs text-[#2563EB] hover:underline">Ver todas →</button>
          </div>
          {(inc.ultimas || []).length === 0 ? <Empty /> : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {inc.ultimas.map(i => (
                <div key={i.id} className="p-3 border border-slate-100 hover:bg-slate-50" style={{borderRadius:8}}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm truncate">{i.titulo}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest font-bold border ${i.estado==="resuelta" ? "text-[#16a34a] border-[#22C55E]/30 bg-[#22C55E]/10" : "text-[#ea580c] border-[#F97316]/30 bg-[#F97316]/10"}`} style={{borderRadius:4}}>{i.estado}</span>
                  </div>
                  {i.descripcion && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{i.descripcion}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">{new Date(i.created_at).toLocaleDateString("es-ES")}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
      <div className="mb-3">
        <h3 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 p-4 flex items-center gap-3 shadow-card" style={{ borderRadius: 12 }}>
      <div className="h-10 w-10 flex items-center justify-center" style={{ background: color + "20", borderRadius: 8 }}>
        <Icon size={20} weight="bold" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 truncate">{label}</div>
        <div className="font-display font-bold text-2xl tracking-tighter" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}
function Empty({ text = "Sin datos" }) {
  return <div className="py-12 text-center text-slate-400 text-sm">{text}</div>;
}
